/**
 * Enumerations observed in the reference product (see docs/product-inventory.md, appendix part 4).
 * Values are kept exactly as the reference shows them because users recognise them.
 */

export const USER_LEVELS = ['admin', 'master', 'distributor', 'retailer', 'employee'] as const;
export type UserLevel = (typeof USER_LEVELS)[number];

/** Level directly above each level in the sales hierarchy. `admin` is tenant-wide (assumption, see roadmap section 8). */
export const LEVEL_PARENT: Record<UserLevel, UserLevel | null> = {
  admin: null,
  master: null,
  distributor: 'master',
  retailer: 'distributor',
  employee: 'retailer',
};

export const LEVEL_LABEL: Record<UserLevel, string> = {
  admin: 'Administrator',
  master: 'Master agent',
  distributor: 'Distributor',
  retailer: 'Retailer',
  employee: 'Employee',
};

export const RECORD_STATUSES = ['Active', 'Inactive'] as const;
export type RecordStatus = (typeof RECORD_STATUSES)[number];

export const GROUP_TYPES = ['System', 'Custom'] as const;
export type GroupType = (typeof GROUP_TYPES)[number];

/** "Record Limit" choices on reference lists, plus 15 used by the inventory list. */
export const PAGE_SIZES = [10, 15, 20, 30, 50, 100, 120, 150, 200] as const;

export const US_STATES = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DC', 'District of Columbia'],
  ['DE', 'Delaware'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['IA', 'Iowa'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['MA', 'Massachusetts'],
  ['MD', 'Maryland'],
  ['ME', 'Maine'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MO', 'Missouri'],
  ['MS', 'Mississippi'],
  ['MT', 'Montana'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['NE', 'Nebraska'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NV', 'Nevada'],
  ['NY', 'New York'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VA', 'Virginia'],
  ['VT', 'Vermont'],
  ['WA', 'Washington'],
  ['WI', 'Wisconsin'],
  ['WV', 'West Virginia'],
  ['WY', 'Wyoming'],
] as const;
export const US_STATE_CODES = US_STATES.map(([code]) => code) as [string, ...string[]];

/* ----------------------------- Plan catalogue ----------------------------- */

export const PLAN_TYPES = [
  'LifeLine',
  'ACP Plan',
  'Combo Plan (Lifeline + ACP)',
  'Prepaid Plan',
  'Postpaid Plan',
] as const;
export type PlanType = (typeof PLAN_TYPES)[number];

export const PLAN_STATE_STATUSES = ['LIVE', 'DEMO'] as const;
export type PlanStateStatus = (typeof PLAN_STATE_STATUSES)[number];

/** Sales channels a plan may be sold through. */
export const SALES_CHANNELS = [
  'CSR',
  'AGENT',
  'API',
  'WEBSITE',
  'APP',
  'IVR',
  'ENROLLMENT PORTAL',
  'DEALER PORTAL',
] as const;
export type SalesChannel = (typeof SALES_CHANNELS)[number];

/** Tax-engine customer types. Only the nine the reference listed first are known. */
export const CUSTOMER_TYPES = [
  ['01', 'RESIDENTIAL'],
  ['02', 'COMMERCIAL'],
  ['03', 'INDUSTRIAL'],
  ['04', 'SENIOR CITIZEN'],
  ['05', 'TRIBAL GOVERNMENT'],
  ['06', 'FEDERAL GOVERNMENT'],
  ['07', 'STATE GOVERNMENT'],
  ['08', 'LOCAL GOVERNMENT'],
  ['09', 'RESERVED'],
] as const;
export const CUSTOMER_TYPE_CODES = CUSTOMER_TYPES.map(([c]) => c) as [string, ...string[]];

export const BUSINESS_TYPES = [
  ['14', 'Prepaid Wireless'],
  ['01', 'Retail'],
  ['10', 'CLEC'],
  ['11', 'IXC'],
  ['12', 'CMRS/Wireless'],
  ['13', 'ILEC'],
  ['15', 'VoIP'],
  ['16', 'TV Provider'],
] as const;
export const BUSINESS_TYPE_CODES = BUSINESS_TYPES.map(([c]) => c) as [string, ...string[]];

export const NLAD_SERVICE_TYPES = [
  'Voice',
  'Broadband',
  'BundledVoice',
  'BundledBroadband',
  'BundledVoiceBroadband',
] as const;

export const GRACE_STATUSES_DURING = ['Suspend', 'Deactivate', 'Activate'] as const;
export const GRACE_STATUSES_AFTER = ['Suspend', 'Deactivate'] as const;

export const CARRIER_CODE_TYPES = ['Service Plan', 'Topup'] as const;
export const TOPUP_PAYMENT_TYPES = ['Credit Card'] as const;
export const TOPUP_TYPES = ['TOPUP'] as const;

/* -------------------------------- Inventory ------------------------------- */

export const UNIT_KINDS = ['sim', 'device'] as const;
export type UnitKind = (typeof UNIT_KINDS)[number];

/** The reference dashboard labels `Free` as "Available". */
export const SIM_STATUSES = ['Free', 'Used', 'Bad', 'Removed', 'Fraud', 'Lost/Stolen'] as const;
export const DEVICE_STATUSES = ['Free', 'Used', 'Bad', 'Removed'] as const;
export type UnitStatus = (typeof SIM_STATUSES)[number];

/**
 * Statuses a unit may only be moved to with a change reason. Assumption: the reference uses
 * its reason list for status changes, but the exact rule was not observed.
 */
export const REASON_REQUIRED_STATUSES: readonly UnitStatus[] = [
  'Bad',
  'Removed',
  'Fraud',
  'Lost/Stolen',
];

export const DEVICE_TYPES = [
  'PHONE',
  'FREEPHONE',
  'WIFI (Hotspot and Connected Devices)',
  'TABLET',
  'WATCH',
  'KIDPHONES',
  'IPAD',
  'IPHONE',
  'LAPTOP',
] as const;
export const PHONE_TYPES = ['Feature', 'Smart'] as const;

export const CHANGE_REASON_STATUSES = ['Free', 'Bad', 'Removed', 'Lost'] as const;
export const CHANGE_PROCESS_TYPES = ['CHANGE ESN', 'CHANGE DEVICE', 'BOTH'] as const;

/**
 * Bulk operations. The reference's Bulk Process menu has 23 entries; `bulk-enrollment` is the
 * Bulk enrollment entry of the orders menu, which runs on the same engine.
 */
export const BULK_OPERATIONS = [
  'add-units',
  'reassign-sim',
  'sim-status-reassign',
  'reassign-device',
  'device-status-reassign',
  'clear-device',
  'clear-sim',
  'reassign-sim-by-agent',
  'reassign-device-by-agent',
  'line-status',
  'deactivate-mdn',
  'swap-sim',
  'swap-mdn',
  'swap-mdn-by-customer',
  'mdn-topup',
  'customer-plan-change',
  'hotline',
  'unhotline',
  'deprovision-sim',
  'print-label',
  'clear-customer-device',
  'customer-update',
  'nlad-process',
  'sim-device-information',
  'bulk-enrollment',
  'bulk-notes',
  'order-fulfillment',
  'shipper-inventory',
  'change-sim-status',
  'untag-device',
  'update-sims',
  'update-devices',
  'adjust-sim',
  'adjust-device',
  'import-customers',
] as const;
export type BulkOperation = (typeof BULK_OPERATIONS)[number];
export const BULK_INPUT_TYPES = ['paste', 'file'] as const;
/** `Uploaded` is a two-step batch waiting for its "Send to ..." step. */
export const BULK_BATCH_STATES = ['Uploaded', 'Processing', 'Completed', 'Failed'] as const;

/**
 * Two-step bulk screens of the reference: the upload only stores the file; the button named
 * here, beside the uploaded batch, runs it.
 */
export const BULK_SEND_LABELS: Partial<Record<BulkOperation, string>> = {
  'deactivate-mdn': 'Send to Disconnect',
  'swap-sim': 'Send to Swap SIM',
  'swap-mdn': 'Send to Swap MDN',
  'swap-mdn-by-customer': 'Send to Swap MDN',
  'mdn-topup': 'Send to MDN Topup',
  'customer-plan-change': 'Send to Customer Plan Change',
  hotline: 'Send to Hotline',
  unhotline: 'Send to Unhotline',
  'deprovision-sim': 'Send to DeProvision',
};

/** NLAD(De-Enroll Type) of Bulk Deactivate MDN; recorded on the row, there is no NLAD link. */
export const NLAD_DEENROLL_TYPES = [
  'deEnrollNonUsage',
  'deEnrollLeaving',
  'deEnrollFailedRecertification',
  'deEnrollDeceased',
  'deEnrollFailedReverification',
] as const;

/**
 * Managed lists stored in the generic `lookups` collection: the lists under Settings > Other
 * Settings and the equipment masters of the Inventory menu. Each is a list of names with an
 * Active/Inactive status that other screens pick from.
 */
export const LOOKUP_KINDS = [
  'disconnectReason',
  'suspendReason',
  'noteType',
  'equipmentType',
  'equipmentStatusName',
] as const;
export type LookupKind = (typeof LOOKUP_KINDS)[number];

/** Screen title and reference permission code of each managed list. */
export const LOOKUP_META: Record<LookupKind, { title: string; code: string; itemLabel: string }> = {
  disconnectReason: { title: 'Disconnection Reason', code: 'PC448', itemLabel: 'Reason' },
  suspendReason: { title: 'Suspend Reason', code: 'PC996', itemLabel: 'Reason' },
  noteType: { title: 'Manage Note Type', code: 'PC450', itemLabel: 'Note Type' },
  equipmentType: {
    title: 'Manage Equipment Type',
    code: 'PC683',
    itemLabel: 'Equipment Type',
  },
  equipmentStatusName: {
    title: 'Manage Equipment Status Name',
    code: 'PC727',
    itemLabel: 'Reason',
  },
};

/** The lists shown under Settings > Other Settings. */
export const OTHER_SETTINGS_KINDS = ['disconnectReason', 'suspendReason', 'noteType'] as const;
